import { Button } from "@package/subframe/components/Button";
import { Loader } from "@package/subframe/components/Loader";
import { Select } from "@package/subframe/components/Select";
import { TextArea } from "@package/subframe/components/TextArea";
import { FeatherCheck, FeatherLoader, FeatherX } from "@subframe/core";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import type { PassFailValue } from "@/components/checks/PassFailControl";
import { PassFailControl } from "@/components/checks/PassFailControl";
import { RegisterEmptyState } from "@/components/logs/RegisterEmptyState";
import { useNursery } from "@/contexts/NurseryContext";
import { useListFireAlarmCallPointsQueryControllerList } from "@/generated/fire-alarm-call-points/fire-alarm-call-points";
import type { UseOfflineLogEntriesResult } from "@/hooks/use-offline-log-entries";
import { useOfflineRegister } from "@/hooks/use-offline-register";
import { FIRE_ALARM_TEST_RESULT_FIELD } from "@/lib/fire-alarm-tests";
import { memberDisplayName } from "@/lib/member-display";

type FireAlarmTestEntry = UseOfflineLogEntriesResult["entries"][number];

interface FireAlarmTestViewProps {
  logTypeId: string;
  entries: FireAlarmTestEntry[];
  isLoading: boolean;
  isOffline: boolean;
  createEntry: UseOfflineLogEntriesResult["createEntry"];
}

/**
 * The weekly fire alarm test (AC21–AC23): one call point per week, so a picker
 * plus a single pass/fail. Record only; which point is due next is out of scope.
 */
export default function FireAlarmTestView({
  logTypeId,
  entries,
  isLoading: entriesLoading,
  isOffline,
  createEntry,
}: FireAlarmTestViewProps) {
  const { selectedNurseryId } = useNursery();
  const callPointsQuery = useListFireAlarmCallPointsQueryControllerList(
    selectedNurseryId ?? "",
    {},
    // Always the current register, so a retired call point can't be picked.
    { query: { enabled: !!selectedNurseryId, refetchOnMount: "always" } },
  );
  const {
    items: callPoints,
    isLoading,
    isServedFromCache,
  } = useOfflineRegister(
    "fire-alarm-call-points",
    selectedNurseryId,
    callPointsQuery,
  );
  // Offline and never loaded on this device: "none registered" would be untrue.
  const isRegisterUnavailable =
    isOffline && !callPointsQuery.data && !isServedFromCache;
  // A queued offline entry only knows its call point's id.
  const callPointNameById = useMemo(
    () => new Map(callPoints.map((point) => [point.id, point.name])),
    [callPoints],
  );

  const [pickedCallPointId, setCallPointId] = useState<string>("");
  const [result, setResult] = useState<PassFailValue>(undefined);
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // One retired since it was picked drops out of the register: back to the
  // placeholder rather than a blank picker.
  const callPointId = callPointNameById.has(pickedCallPointId)
    ? pickedCallPointId
    : "";

  const canSubmit = !!callPointId && result !== undefined && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const { outcome, error } = await createEntry(
        {
          logTypeId,
          data: { [FIRE_ALARM_TEST_RESULT_FIELD]: result === "pass" },
          notes: notes.trim() || undefined,
          fireAlarmCallPointId: callPointId,
        },
        { awaitSync: true },
      );
      if (outcome === "failed") {
        // Keep the form filled so the corrected retry needs no re-entry. The
        // usual cause is a call point retired since the list loaded.
        toast.error(error ?? "Failed to record the test — please try again");
        void callPointsQuery.refetch();
        return;
      }
      toast.success(
        outcome === "queued"
          ? "Weekly test saved — it will sync when you're back online"
          : "Weekly test recorded",
      );
      setResult(undefined);
      setNotes("");
    } catch {
      toast.error("Failed to record the test — please try again");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <Loader size="large" />
      </div>
    );
  }

  if (callPoints.length === 0) {
    return (
      <RegisterEmptyState
        isRegisterUnavailable={isRegisterUnavailable}
        emptyMessage="No fire alarm call points are registered for this nursery yet. Ask a group admin to add them in the nursery's settings."
      />
    );
  }

  return (
    <div className="flex w-full flex-col items-start gap-4">
      {isOffline && (
        <div className="flex w-full items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <span className="text-body font-body text-default-font">
            You&apos;re offline. This test will sync when you reconnect.
          </span>
        </div>
      )}

      <div className="flex w-full flex-col items-start gap-3 rounded-lg border border-neutral-200 p-4">
        {/* Subframe's Select label is a plain span, not tied to its trigger;
            a wrapping <label> gives the trigger button its accessible name. */}
        <label className="flex w-full flex-col items-start gap-3">
          <span className="text-body-bold font-body-bold text-default-font">
            Call point tested
          </span>

          <Select
            className="w-full"
            placeholder="Select the call point you tested"
            value={callPointId}
            onValueChange={setCallPointId}
            disabled={isSubmitting}
          >
            {callPoints.map((point) => (
              <Select.Item key={point.id} value={point.id}>
                {point.room ? `${point.name} (${point.room.name})` : point.name}
              </Select.Item>
            ))}
          </Select>
        </label>

        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <span className="text-body font-body text-default-font">
            Did the alarm sound?
          </span>
          <PassFailControl
            label="Alarm sounded"
            value={result}
            onChange={setResult}
            disabled={isSubmitting}
          />
        </div>

        <TextArea className="h-auto w-full" label="Notes (optional)">
          <TextArea.Input
            placeholder="Note anything that needs attention"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isSubmitting}
          />
        </TextArea>

        <div className="flex w-full items-center justify-end">
          <Button
            type="button"
            variant="brand-primary"
            size="large"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isSubmitting ? "Recording..." : "Record test"}
          </Button>
        </div>
      </div>

      <div className="flex w-full flex-col gap-3">
        <span className="text-body-bold font-body-bold text-default-font">
          Today&apos;s tests
        </span>

        {entriesLoading ? (
          <div className="flex w-full items-center justify-center py-8">
            <Loader size="medium" />
          </div>
        ) : entries.length === 0 ? (
          <div className="flex w-full flex-col items-center justify-center gap-1 py-8">
            <span className="text-body font-body text-subtext-color">
              No tests recorded yet
            </span>
          </div>
        ) : (
          <div className="flex w-full flex-col gap-2">
            {entries.map((entry) => (
              <FireAlarmTestEntryRow
                key={entry.id}
                entry={entry}
                callPointName={
                  entry.fireAlarmCallPoint?.name ||
                  (entry.fireAlarmCallPoint?.id
                    ? (callPointNameById.get(entry.fireAlarmCallPoint.id) ?? "")
                    : "")
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FireAlarmTestEntryRow({
  entry,
  callPointName,
}: {
  entry: FireAlarmTestEntry;
  callPointName: string;
}) {
  const passed =
    (entry.data as Record<string, unknown>)[FIRE_ALARM_TEST_RESULT_FIELD] ===
    true;
  const isPending = entry.localSyncState === "pending";

  return (
    <div
      className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 ${
        passed ? "border-neutral-200 bg-neutral-50" : "border-red-200 bg-red-50"
      }`}
    >
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          passed ? "bg-green-100" : "bg-red-100"
        }`}
      >
        {passed ? (
          <FeatherCheck className="h-4 w-4 text-green-600" />
        ) : (
          <FeatherX className="h-4 w-4 text-red-600" />
        )}
      </div>
      <div className="flex grow flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="text-body-bold font-body-bold text-default-font">
            {format(new Date(entry.timestamp), "HH:mm")}
          </span>
          <span
            className={`text-caption-bold font-caption-bold ${passed ? "text-green-600" : "text-red-600"}`}
          >
            {passed ? "Pass" : "Fail"}
          </span>
          {isPending && (
            <FeatherLoader className="h-3 w-3 animate-spin text-subtext-color" />
          )}
        </div>
        <span className="text-caption font-caption text-subtext-color">
          {callPointName}
        </span>
        {entry.notes && (
          <span className="text-caption font-caption text-subtext-color italic">
            {entry.notes}
          </span>
        )}
      </div>
      <span className="text-caption font-caption text-subtext-color">
        {memberDisplayName(entry.recordedBy)}
      </span>
    </div>
  );
}
